import { AlertTriangle, Search } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { mapFamilySummaries } from '@/features/families/mappers';
import { queryKeys } from '@/query/keys';
import type { FamilySummaryViewModel } from '@/models/family';

import { FamilyCandidatePicker } from './FamilyCandidatePicker';

interface AddToFamilyDialogProps {
  readonly patientGuid: string;
  readonly onCancel: () => void;
  readonly onSuccess: (familyGuid: string) => void;
}

const MAX_CANDIDATES = 8;
const MIN_QUERY_LEN = 2;

/**
 * Modal that attaches the current patient to an existing family.
 *
 * Same explicit search affordance as the create-family wizard's Step 0
 * (`Step0QuickMatch`) — input + Search button, also fires on Enter — so it's
 * unambiguous when the request goes out. Auto-debounced fetch-on-keystroke
 * was tried first but was hard to observe in DevTools when the user is
 * checking whether the API was hit; explicit click matches the pattern
 * already established elsewhere in the family flows.
 *
 * On submit (selection + Add) the patient is attached via
 * `POST /families/{guid}/members` with `person_id = patient.guid`; both the
 * patient and family queries are invalidated so the right-rail panel
 * re-renders in the linked state.
 */
export function AddToFamilyDialog({
  patientGuid,
  onCancel,
  onSuccess,
}: AddToFamilyDialogProps): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const search = useMutation<readonly FamilySummaryViewModel[], Error, string>({
    mutationFn: async (q) => {
      const rows = await apiClient.searchFamilies({ name: q });
      return mapFamilySummaries(rows).slice(0, MAX_CANDIDATES);
    },
    onSuccess: () => {
      setHasSearched(true);
    },
  });

  const trimmed = name.trim();
  const canSearch = trimmed.length >= MIN_QUERY_LEN;
  const candidates: readonly FamilySummaryViewModel[] = search.data ?? [];

  const handleSearch = (): void => {
    if (!canSearch) return;
    setSelectedGuid(null);
    search.mutate(trimmed);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && canSearch) {
      e.preventDefault();
      handleSearch();
    }
  };

  const attach = useMutation<{ familyGuid: string }, Error, { familyGuid: string }>({
    meta: { successMessage: 'success.toast.title.family_member_added' },
    mutationFn: async ({ familyGuid }) => {
      await apiClient.addFamilyMember({
        guid: familyGuid,
        body: {
          cascade_role: 'FAMILY_MEMBER',
          person_id: patientGuid,
        },
      });
      return { familyGuid };
    },
    onSuccess: ({ familyGuid }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.family(familyGuid) });
      void queryClient.invalidateQueries({ queryKey: ['person', patientGuid] });
      // Browse Families shows a member-count column that just changed.
      void queryClient.invalidateQueries({ queryKey: ['recent-families'] });
      onSuccess(familyGuid);
    },
  });

  const busy = attach.isPending;
  const canSubmit = selectedGuid !== null && !busy;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onCancel();
      }}
    >
      <DialogContent className="max-w-lg" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('patient.sections.family.addToExisting.title')}</DialogTitle>
          <DialogDescription>
            {t('patient.sections.family.addToExisting.description')}
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit || selectedGuid === null) return;
            attach.mutate({ familyGuid: selectedGuid });
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="add-to-family-name">
              {t('patient.sections.family.addToExisting.searchLabel')}
            </Label>
            <div className="grid grid-cols-[1fr_auto] gap-2 items-stretch">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  id="add-to-family-name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setHasSearched(false);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={t('patient.sections.family.addToExisting.searchPlaceholder')}
                  autoComplete="off"
                  className="pl-8"
                />
              </div>
              <Button
                type="button"
                onClick={handleSearch}
                disabled={!canSearch || search.isPending}
                loading={search.isPending}
              >
                {t('patient.sections.family.addToExisting.searchButton')}
              </Button>
            </div>
          </div>

          <FamilyCandidatePicker
            candidates={candidates}
            selectedGuid={selectedGuid}
            onSelect={setSelectedGuid}
            loading={search.isPending}
            isError={search.isError}
            hasQuery={hasSearched}
            showMinLengthHint={!canSearch && trimmed.length > 0 && !hasSearched}
            minQueryLen={MIN_QUERY_LEN}
            candidatesAriaLabel={t('patient.sections.family.addToExisting.candidatesAria')}
          />

          {attach.isError && (
            <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-3">
              <AlertTriangle
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive"
                aria-hidden="true"
              />
              <p className="text-xs text-destructive">{t('app.unknownError')}</p>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
              {t('ui.dialog.close')}
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              loading={busy}
              loadingText={t('patient.dirtyFooter.saving')}
            >
              {t('patient.sections.family.addToExisting.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
